import { NextPage } from "next";
import React, { ReactNode, useEffect, useState } from "react";
import {
  HydrationBoundary,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { isProd } from "@/Env";
import Script from "next/script";
import { GlobalRouterLoadingWebView } from "@/components/webview/shared/GlobalRouterLoadingWebView";
import { useRouter } from "next/router";
import { pageview, GtagScript } from "@/libs/analytics";
import FloatingButtonWebView from "@/components/webview/shared/FloatingButtonWebView";
import { isInAppWebView } from "@/components/webview/utils/webviewUserAgnetUtils";

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactNode) => ReactNode;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
    },
  },
});

/*
 web view layout hoc
 */
export function withWebViewLayout<P extends object>(
  webViewComponents: NextPage<P>,
): NextPageWithLayout {
  const ComponentWithLayout = webViewComponents as NextPageWithLayout;
  ComponentWithLayout.getLayout = (page) => {
    return (
      <QueryClientProvider client={queryClient}>
        <HydrationBoundary
          state={(page as any)?.props?.pageProps?.dehydratedState}
        >
          <WebViewGuard>{page}</WebViewGuard>
          <FloatingButtonWebView />
          <GlobalRouterLoadingWebView />
          <AnalyticsScriptWebView />
          <ReactQueryDevtools />
        </HydrationBoundary>
      </QueryClientProvider>
    );
  };
  return ComponentWithLayout;
}

/*
 webView 환경이 아니면 initUrl 리다이렉트
 */
function WebViewGuard({ children }: { children: ReactNode }) {
  const [allowed, setAllowed] = useState(false);
  const initUrl = isProd
    ? "https://www.bhaptics.com/"
    : "https://dev.bhaptics.com/";

  useEffect(() => {
    if (isInAppWebView() || !isProd) {
      setAllowed(true);
    } else {
      window.location.href = initUrl;
    }
  }, []);

  if (!allowed) {
    return null;
  }

  return <>{children}</>;
}

/*
 Google Analytics + Clarity Script + Page View Hook
 */
function AnalyticsScriptWebView() {
  const router = useRouter();

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      pageview(url);
    };

    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router]);

  return (
    <>
      <GtagScript />
      {/* Microsoft Clarity */}
      <Script
        id="clarity-init"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            (function(c,l,a,r,i,t,y){
              c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
              t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
              y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
            })(window, document, "clarity", "script", "s92qv5qzdw");
          `,
        }}
      />
    </>
  );
}
